import IParser from "@/types/IParser"
import ParseError from "@/types/ParseError"
import dayjs from "dayjs"
import { Tariff } from "./tariff"
import { ITariff, IProductDetails, IUnitRates } from "./tariff.types"
import { GetTariffsQuery } from "@/api/hasura/generated/graphql"
import { SO_GREEN_TRACKER_TARIFF_CODE } from "@/constants/tariff"
import tariffHelper from "@/helpers/tariffHelper"

// Hasura current_future_product_details shape — used only by TariffService / HasuraTariffParser
type IHasuraTariffRaw =
  GetTariffsQuery["current_future_product_details"][number]

export type TariffRateRaw = { rate_name: string; rate_value: number }

export const getBundleGroupCode = (
  code: string | null | undefined,
  bundleGroupCode: string | null | undefined,
): string =>
  code?.includes(SO_GREEN_TRACKER_TARIFF_CODE) ? "VAR" : (bundleGroupCode ?? "")

const capitalise = (s: string) => s.charAt(0).toUpperCase() + s.slice(1)

export const parseTariffRates = (
  rates: TariffRateRaw[] | null | undefined,
): IUnitRates | undefined => {
  if (!rates?.length) return undefined
  return Object.fromEntries(
    rates.map(({ rate_name, rate_value }) => [
      capitalise(rate_name),
      rate_value,
    ]),
  ) as IUnitRates
}

// The view's vat_rate is a numeric scalar, which can arrive as a string — coerce
// so downstream rate comparisons are numeric, not string-vs-number. Anything not
// genuinely numeric stays undefined ("unknown"): blanks, booleans and arrays all
// coerce to 0 via Number(), which would read as a VAT-free claim.
const parseVatRate = (value: unknown): number | undefined => {
  if (typeof value === "number") {
    return Number.isFinite(value) ? value : undefined
  }
  if (typeof value === "string" && value.trim() !== "") {
    const parsed = Number(value)
    return Number.isFinite(parsed) ? parsed : undefined
  }
  return undefined
}

const parseHasuraProductDetails = (data: IHasuraTariffRaw): IProductDetails => {
  const isGas = data.code ? tariffHelper.isGas(data.code) : false
  const rates = parseTariffRates(data.tariff_rates)
  return {
    name: data.name ?? "",
    fromDttm: dayjs(data.from_dt),
    unitRate: isGas ? rates?.Standard : undefined,
    unitRates: isGas ? undefined : rates,
    standingCharge: data.charge_value ?? 0,
    earlyTerminationCharge: data.early_termination_charge ?? 0,
    tariffComparisonCode: getBundleGroupCode(
      data.code,
      data.bundle_dfn_group_code,
    ),
    tariffComparisonRateName: data.bundle_df_type_name ?? "",
    vatRate: parseVatRate(data.vat_rate),
  }
}

export const HasuraTariffParser: IParser<ITariff, IHasuraTariffRaw> = {
  toDomain: (data: IHasuraTariffRaw) => {
    try {
      return new Tariff({
        id: data.id ?? 0,
        code: data.code ?? "",
        gsp: data.gsp_group ?? "",
        display: data.display_name ?? "",
        productDetails: parseHasuraProductDetails(data),
        isPrepay: false,
        isSmartPrepay: false,
        isAvailableToPublic: true,
        directDebit: data.direct_debit ?? false,
        durationMonths: data.duration_value ?? null,
        createdAt: dayjs(data.variant_from_dt),
      })
    } catch (error) {
      throw new ParseError(error)
    }
  },
}
